import type { Layer, TemplateDoc } from '../template';
import { CAMERA_PRESET_MAP, cameraFrames } from './camera';
import { resolveEasing } from './easing';
import { FX_KIND_MAP, fxDefaults, type FxFont, type FxNode } from './fx';
import { keyAnimations } from './keys';
import { readingOrder } from './order';
import { layerScene } from './scenes';
import { docPalette } from './palette';
import { poseFrames, reversePoses, type CssKeyframe, type Pose } from './pose';
import { EMPHASIS_PRESET_MAP, ENTER_PRESET_MAP, type PresetParams } from './presets';
import type { DocMotion, MotionSplit, MotionStep } from './schema';
import { TRANSITION_MAP, type Box } from './transitions';

/**
 * Compiles the declarative motion of a template into a plan of Web Animations: plain keyframes
 * and timings, nothing browser-specific, so it is unit-testable and every player (editor
 * preview, render page, HTML5 export) plays exactly the same thing.
 */

export interface PlannedAnimation {
  keyframes: CssKeyframe[];
  /** Start time in ms from the beginning of the video. */
  start: number;
  duration: number;
  easing: string;
  fill: FillMode;
  iterations: number;
  composite: 'replace' | 'add';
  /** Play on each text piece (line/word/letter) with the layer's stagger instead of the box. */
  pieces?: boolean;
  /** For pieces: `start` is when the last piece must finish (exits), not when the first starts. */
  anchorEnd?: boolean;
}

type FillMode = 'none' | 'forwards' | 'backwards' | 'both';

export interface LayerPlan {
  layerId: string;
  animations: PlannedAnimation[];
  split?: MotionSplit;
  /** Visible only in this window (scene layers). */
  window?: [number, number];
}

export interface PlannedScene {
  id: string;
  start: number;
  end: number;
}

/** Generated motion graphics: nodes and their animations, times relative to `start`. */
export interface FxPlan {
  id: string;
  kind: string;
  z: 'front' | 'back';
  start: number;
  duration: number;
  nodes: FxNode[];
}

export interface MotionPlan {
  duration: number;
  fps: number;
  loops: number;
  scenes: PlannedScene[];
  layers: LayerPlan[];
  fx: FxPlan[];
}

export interface CompileOptions {
  /** Layout boxes (canvas px) of laid-out layers; needed for canvas-space transitions. */
  boxOf?: (layerId: string) => Box | undefined;
}

export function sceneTimeline(motion: DocMotion): { scenes: PlannedScene[]; duration: number } {
  if (!motion.scenes.length) return { scenes: [], duration: motion.duration };
  const scenes: PlannedScene[] = [];
  let start = 0;
  motion.scenes.forEach((s, i) => {
    const end = start + s.duration;
    scenes.push({ id: s.id, start, end });
    const next = motion.scenes[i + 1];
    if (!next) return;
    const td = transitionDuration(motion, i);
    start = end - td;
  });
  return { scenes, duration: scenes[scenes.length - 1].end };
}

/** Transition from scene i into i + 1, clamped so it never eats a whole scene. */
export function transitionDuration(motion: DocMotion, i: number): number {
  const s = motion.scenes[i];
  const next = motion.scenes[i + 1];
  if (!s?.transition || !next) return 0;
  const preset = TRANSITION_MAP.get(s.transition.preset);
  if (!preset || preset.id === 'cut') return 0;
  return Math.max(0, Math.min(s.transition.duration, s.duration / 2, next.duration / 2));
}

function blurScaleOf(doc: TemplateDoc): number {
  return Math.max(0.15, Math.min(doc.width, doc.height) / 1080);
}

function presetPoses(
  step: MotionStep,
): { poses: Pose[]; easing: string; exitEasing: string } | null {
  const preset = ENTER_PRESET_MAP.get(step.preset);
  if (!preset) return null;
  const params: PresetParams = {};
  for (const p of preset.params ?? []) params[p.key] = p.default;
  Object.assign(params, step.params ?? {});
  return {
    poses: preset.poses({ params }),
    easing: preset.easing,
    exitEasing: preset.exitEasing ?? preset.easing,
  };
}

function hasMotion(layer: Layer): boolean {
  const m = layer.motion;
  return Boolean(m && (m.enter || m.exit || m.emphasis || m.scene || m.keys?.length));
}

export function compileMotion(doc: TemplateDoc, opts: CompileOptions = {}): MotionPlan {
  const motion = doc.motion;
  if (!motion) return { duration: 0, fps: 30, loops: 1, scenes: [], layers: [], fx: [] };
  const { scenes, duration } = sceneTimeline(motion);
  const sceneById = new Map(scenes.map((s) => [s.id, s]));
  const sceneIndex = new Map(motion.scenes.map((s, i) => [s.id, i]));
  const blurScale = blurScaleOf(doc);
  const layers: LayerPlan[] = [];

  for (const layer of doc.layers) {
    if (!hasMotion(layer)) continue;
    const m = layer.motion!;
    // Layers inside a stack follow the scene of the stack
    const sceneId = m.scene ?? layerScene(doc, layer);
    const scene = sceneId ? sceneById.get(sceneId) : undefined;
    const from = scene?.start ?? 0;
    const to = scene?.end ?? duration;
    const plan: LayerPlan = { layerId: layer.id, animations: [] };
    const split = m.split && m.split.by !== 'none' && layer.type === 'text' ? m.split : undefined;
    if (split) plan.split = split;
    if (scene) plan.window = [scene.start, scene.end];
    const base = { baseRotation: layer.rotation, baseOpacity: layer.opacity, blurScale };

    // Hand-placed key frames come first: the presets' fades replace their opacity while playing
    for (const k of keyAnimations(m.keys, layer.opacity, blurScale))
      plan.animations.push({ ...k, easing: 'linear', fill: 'both', iterations: 1 });

    if (m.enter) {
      const p = presetPoses(m.enter);
      if (p) {
        const start = from + m.enter.delay;
        plan.animations.push({
          keyframes: poseFrames(
            p.poses,
            split ? { ...base, baseRotation: 0, baseOpacity: 1 } : base,
          ),
          start,
          duration: m.enter.duration,
          easing: resolveEasing(m.enter.easing, resolveEasing(p.easing)),
          fill: 'backwards',
          iterations: 1,
          composite: 'replace',
          pieces: Boolean(split),
        });
      }
    }

    let exitStart = to;
    if (m.exit) {
      const p = presetPoses(m.exit);
      if (p) {
        const end = Math.max(from, to - m.exit.delay);
        const start = Math.max(from, end - m.exit.duration);
        exitStart = start;
        plan.animations.push({
          keyframes: poseFrames(
            reversePoses(p.poses),
            split ? { ...base, baseRotation: 0, baseOpacity: 1 } : base,
          ),
          start: split ? end : start,
          duration: Math.max(16, end - start),
          easing: resolveEasing(m.exit.easing, resolveEasing(p.exitEasing)),
          fill: 'forwards',
          iterations: 1,
          composite: 'replace',
          pieces: Boolean(split),
          anchorEnd: Boolean(split),
        });
      }
    }

    if (m.emphasis) {
      const preset = EMPHASIS_PRESET_MAP.get(m.emphasis.preset);
      if (preset) {
        const params: PresetParams = {};
        for (const p of preset.params ?? []) params[p.key] = p.default;
        Object.assign(params, m.emphasis.params ?? {});
        const poses = preset.poses({ params });
        const start = from + m.emphasis.delay;
        const cycle = m.emphasis.duration + m.emphasis.gap;
        const available = Math.max(0, exitStart - start);
        const iterations =
          m.emphasis.repeat > 0
            ? m.emphasis.repeat
            : Math.max(1, Math.floor((available + m.emphasis.gap) / cycle));
        // Pause between repetitions: squeeze the poses into the first part of each cycle
        const k = cycle > 0 ? m.emphasis.duration / cycle : 1;
        const timed = poses.map((p, i) => ({
          ...p,
          offset: (p.offset ?? (poses.length > 1 ? i / (poses.length - 1) : 0)) * k,
        }));
        if (k < 1) timed.push({ ...poses[poses.length - 1], offset: 1, easing: undefined });
        // Transform-only moves stack on top of the enter/exit; opacity flashes replace
        const additive = !poses.some((p) => p.opacity !== undefined);
        plan.animations.push({
          keyframes: poseFrames(timed, additive ? { additive: true, blurScale } : base),
          start,
          duration: cycle,
          easing: resolveEasing(m.emphasis.easing, resolveEasing(preset.easing)),
          fill: 'none',
          iterations,
          composite: additive ? 'add' : 'replace',
        });
      }
    }

    // Scene transitions: the layers of a scene take part in its transitions in and out
    if (scene && m.scene) {
      const i = sceneIndex.get(m.scene)!;
      const box = opts.boxOf?.(layer.id) ?? {
        x: layer.x,
        y: layer.y,
        width: layer.width,
        height: layer.height,
      };
      const ctx = (params: PresetParams = {}) => ({
        canvas: { width: doc.width, height: doc.height },
        box,
        params,
      });
      const outgoing = motion.scenes[i];
      const tdOut = transitionDuration(motion, i);
      if (outgoing?.transition && tdOut > 0) {
        const t = TRANSITION_MAP.get(outgoing.transition.preset)!;
        const fr = t.frames(ctx(withDefaults(t.params, outgoing.transition.params)));
        if (fr.out.length) {
          plan.animations.push({
            keyframes: fr.out,
            start: scene.end - tdOut,
            duration: tdOut,
            easing:
              t.cover && t.easing === 'linear'
                ? 'linear'
                : resolveEasing(outgoing.transition.easing, resolveEasing(t.easing)),
            fill: 'forwards',
            iterations: 1,
            composite: fr.composite ?? 'replace',
          });
        }
      }
      const incoming = i > 0 ? motion.scenes[i - 1] : undefined;
      const tdIn = i > 0 ? transitionDuration(motion, i - 1) : 0;
      if (incoming?.transition && tdIn > 0) {
        const t = TRANSITION_MAP.get(incoming.transition.preset)!;
        const fr = t.frames(ctx(withDefaults(t.params, incoming.transition.params)));
        if (fr.in.length) {
          plan.animations.push({
            keyframes: fr.in,
            start: scene.start,
            duration: tdIn,
            easing:
              t.cover && t.easing === 'linear'
                ? 'linear'
                : resolveEasing(incoming.transition.easing, resolveEasing(t.easing)),
            fill: 'backwards',
            iterations: 1,
            composite: fr.composite ?? 'replace',
          });
        }
      }
    }

    if (plan.animations.length || plan.window) layers.push(plan);
  }

  addCamera(doc, motion, layers, opts, blurScale);
  const fx = compileFx(doc, motion, scenes, opts);
  hideSources(doc, layers);
  return { duration, fps: motion.fps, loops: motion.loops, scenes, layers, fx };
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

function layerBox(layer: Layer, opts: CompileOptions): Box {
  return (
    opts.boxOf?.(layer.id) ?? { x: layer.x, y: layer.y, width: layer.width, height: layer.height }
  );
}

/** Camera moves become additive moves of every top-level layer, created after its own moves. */
function addCamera(
  doc: TemplateDoc,
  motion: DocMotion,
  layers: LayerPlan[],
  opts: CompileOptions,
  blurScale: number,
) {
  const steps = (motion.camera ?? []).filter((c) => CAMERA_PRESET_MAP.has(c.preset));
  if (!steps.length) return;
  const canvas = { width: doc.width, height: doc.height };
  const byId = new Map(layers.map((l) => [l.layerId, l]));
  for (const layer of doc.layers) {
    if (layer.parentId || !layer.visible) continue;
    let plan = byId.get(layer.id);
    if (!plan) {
      plan = { layerId: layer.id, animations: [] };
      layers.push(plan);
      byId.set(layer.id, plan);
    }
    const box = layerBox(layer, opts);
    for (const step of steps) {
      const preset = CAMERA_PRESET_MAP.get(step.preset)!;
      const poses = preset.poses({
        canvas,
        params: withDefaults(preset.params, step.params),
        seed: hash(step.id),
      });
      const still = (p: (typeof poses)[number]) =>
        (p.k ?? 1) === 1 && !p.x && !p.y && !p.rot && !p.blur;
      const before = !still(poses[0]) && step.start <= 0;
      const after = !still(poses[poses.length - 1]);
      plan.animations.push({
        keyframes: cameraFrames(poses, box, canvas, blurScale),
        start: step.start,
        duration: step.duration,
        easing: resolveEasing(step.easing, resolveEasing(preset.easing)),
        fill: before && after ? 'both' : before ? 'backwards' : after ? 'forwards' : 'none',
        iterations: 1,
        composite: 'add',
      });
    }
  }
}

/** The biggest visible text: the default text of title cards and marquees. */
function headline(doc: TemplateDoc): Extract<Layer, { type: 'text' }> | undefined {
  let best: Extract<Layer, { type: 'text' }> | undefined;
  for (const l of doc.layers)
    if (
      l.type === 'text' &&
      l.visible &&
      // Words or a number, not a decorative quote mark
      /\p{L}{2,}|\d/u.test(l.text) &&
      (!best || l.fontSize > best.fontSize)
    )
      best = l;
  return best;
}

/** Visible text of the design in reading order (top to bottom, left to right), one per line. */
function copyOf(doc: TemplateDoc): string {
  return readingOrder(doc)
    .filter(
      (l): l is Extract<Layer, { type: 'text' }> =>
        l.type === 'text' && l.visible && /\p{L}{2,}|\d/u.test(l.text),
    )
    .map((l) => l.text.replace(/\s+/g, ' ').trim())
    .join('\n');
}

function compileFx(
  doc: TemplateDoc,
  motion: DocMotion,
  scenes: PlannedScene[],
  opts: CompileOptions,
): FxPlan[] {
  const items = activeFx(doc).map((f) => ({ ...f, params: f.params ?? {} }));
  // Cover effects of scene transitions
  motion.scenes.forEach((s, i) => {
    const td = transitionDuration(motion, i);
    const t = s.transition ? TRANSITION_MAP.get(s.transition.preset) : undefined;
    if (!t?.cover || td <= 0) return;
    items.push({
      id: `transition-${s.id}`,
      kind: t.cover.kind,
      start: scenes[i].end - td,
      duration: td,
      z: 'front',
      params: { ...t.cover.params, ...withDefaults(t.params, s.transition!.params) },
    });
  });
  if (!items.length) return [];
  const palette = docPalette(doc);
  const head = headline(doc);
  const font: FxFont = head
    ? {
        family: head.fontFamily,
        weight: head.fontWeight,
        style: head.fontStyle,
        transform: head.textTransform,
      }
    : { family: 'Inter', weight: 800, style: 'normal', transform: 'none' };
  const byId = new Map(doc.layers.map((l) => [l.id, l]));
  const out: FxPlan[] = [];
  for (const item of items) {
    const kind = FX_KIND_MAP.get(item.kind);
    if (!kind) continue;
    const params = fxDefaults(kind, item.params);
    const targetId = typeof params.target === 'string' ? params.target : '';
    const target =
      (targetId && byId.get(targetId)) ||
      (kind.params.some((p) => p.type === 'layer') && kind.id === 'underline' ? head : undefined);
    // Bursts and rings centred on a layer
    const anchor = typeof params.anchor === 'string' ? byId.get(params.anchor) : undefined;
    if (anchor) {
      const b = layerBox(anchor, opts);
      params.x = Math.round(((b.x + b.width / 2) / doc.width) * 1000) / 10;
      params.y = Math.round(((b.y + b.height / 2) / doc.height) * 1000) / 10;
    }
    // Text effects: the text, font and colour of a chosen text layer, or a font of their own
    const source = sourceLayer(byId, params);
    let itemFont = source
      ? {
          family: plainFamily(source.fontFamily),
          weight: source.fontWeight,
          style: source.fontStyle,
          transform: source.textTransform,
        }
      : font;
    if (typeof params.font === 'string' && params.font.trim())
      itemFont = { ...itemFont, family: plainFamily(params.font.trim()) };
    const weight = Number(params.weight);
    if (Number.isFinite(weight) && weight >= 100 && weight <= 1000)
      itemFont = { ...itemFont, weight };
    if (source && item.params.color === undefined) params.color = source.color;
    // The layer's font size is the effect's size (unless the effect sets its own)
    if (source && item.params.size === undefined && SIZE_FROM_FONT.has(kind.id))
      params.size = Math.round((plainSize(source) / Math.min(doc.width, doc.height)) * 1000) / 10;
    const nodes = kind.build({
      canvas: { width: doc.width, height: doc.height },
      palette,
      params,
      duration: item.duration,
      seed: hash(item.id),
      text: source?.text ?? head?.text ?? '',
      copy: copyOf(doc),
      font: itemFont,
      box: target ? layerBox(target, opts) : source ? layerBox(source, opts) : undefined,
    });
    out.push({
      id: item.id,
      kind: kind.id,
      z: item.z ?? kind.z,
      start: item.start,
      duration: item.duration,
      nodes,
    });
  }
  return out;
}

type TextLayerOf = Extract<Layer, { type: 'text' }>;

/**
 * Text effects kept on text layers (`motion.effect`) as motion graphics items: timed from the
 * layer's appearance, drawn with its text and font (`source`).
 */
export function layerEffectItems(doc: TemplateDoc): DocMotion['fx'] {
  const motion = doc.motion;
  if (!motion) return [];
  const { scenes, duration } = sceneTimeline(motion);
  const byId = new Map(scenes.map((s) => [s.id, s]));
  const out: DocMotion['fx'] = [];
  for (const layer of doc.layers) {
    const e = layer.motion?.effect;
    if (!e || layer.type !== 'text' || !layer.visible) continue;
    const sceneId = layerScene(doc, layer);
    const scene = sceneId ? byId.get(sceneId) : undefined;
    const from = scene?.start ?? 0;
    const to = scene?.end ?? duration;
    const start = Math.min(Math.max(from, from + e.delay), Math.max(from, to - 16));
    const length = Math.max(16, Math.min(e.duration ?? to - start, to - start));
    out.push({
      id: `effect-${layer.id}`,
      kind: e.kind,
      start,
      duration: length,
      z: 'front',
      params: { ...(e.params ?? {}), source: layer.id },
    });
  }
  return out;
}

/** Every motion graphics item that plays: the document's own (not switched off) and layer effects. */
export function activeFx(doc: TemplateDoc): DocMotion['fx'] {
  return [...(doc.motion?.fx ?? []).filter((f) => !f.hidden), ...layerEffectItems(doc)];
}

/** The text layer a text effect takes its text and font from (`params.source`). */
function sourceLayer(byId: Map<string, Layer>, params: PresetParams): TextLayerOf | undefined {
  const id = typeof params.source === 'string' ? params.source : '';
  const l = id ? byId.get(id) : undefined;
  return l?.type === 'text' ? l : undefined;
}

/** Effects whose `size` is the size of the type, in % of the canvas's short side. */
const SIZE_FROM_FONT = new Set([
  'words',
  'slice-text',
  'extrude',
  'glitch-text',
  'neon',
  'terminal',
]);

function plainSize(l: TextLayerOf): number {
  return l.fontSize;
}

/** A font family from a brand font reference ("brand:font-heading|Inter") or a family. */
function plainFamily(family: string): string {
  return family.startsWith('brand:') ? family.slice(family.indexOf('|') + 1) || 'Inter' : family;
}

/**
 * A text layer that a text effect redraws is hidden while the effect plays (otherwise the text
 * would show twice).
 */
function hideSources(doc: TemplateDoc, layers: LayerPlan[]) {
  const byId = new Map(doc.layers.map((l) => [l.id, l]));
  const plans = new Map(layers.map((p) => [p.layerId, p]));
  for (const item of activeFx(doc)) {
    const kind = FX_KIND_MAP.get(item.kind);
    if (!kind || !kind.params.some((p) => p.key === 'source')) continue;
    const params = fxDefaults(kind, item.params);
    const source = sourceLayer(byId, params);
    if (!source || params.hideSource === 'off') continue;
    let plan = plans.get(source.id);
    if (!plan) {
      plan = { layerId: source.id, animations: [] };
      layers.push(plan);
      plans.set(source.id, plan);
    }
    // Created last, so it wins over the layer's own fades while it plays
    plan.animations.push({
      keyframes: [{ opacity: 0 }, { opacity: 0 }],
      start: item.start,
      duration: Math.max(16, item.duration),
      easing: 'linear',
      fill: 'none',
      iterations: 1,
      composite: 'replace',
    });
  }
}

function withDefaults(
  defs: Array<{ key: string; default: number | string }> | undefined,
  params: PresetParams | undefined,
): PresetParams {
  const out: PresetParams = {};
  for (const d of defs ?? []) out[d.key] = d.default;
  return Object.assign(out, params ?? {});
}

/** Order in which split pieces start: rank of each piece index. */
export function pieceRanks(count: number, order: MotionSplit['order'], seed = 1): number[] {
  const idx = Array.from({ length: count }, (_, i) => i);
  switch (order) {
    case 'end':
      return idx.map((i) => count - 1 - i);
    case 'center': {
      const mid = (count - 1) / 2;
      return idx.map((i) => Math.round(Math.abs(i - mid)));
    }
    case 'edges': {
      const mid = (count - 1) / 2;
      return idx.map((i) => Math.round(mid - Math.abs(i - mid)));
    }
    case 'random': {
      // Deterministic shuffle: renders must be reproducible frame by frame
      let s = seed >>> 0 || 1;
      const rnd = () => (s = (s * 1664525 + 1013904223) >>> 0) / 4294967296;
      const order = [...idx];
      for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(rnd() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }
      const ranks = new Array<number>(count);
      order.forEach((piece, rank) => (ranks[piece] = rank));
      return ranks;
    }
    default:
      return idx;
  }
}
